import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Plus, ChevronLeft, ChevronRight, Calendar, Coffee, Sun, Moon, Cookie, Dumbbell, Trash2 } from 'lucide-react';

const mealIcons = {
  Breakfast: Coffee,
  Lunch: Sun,
  Dinner: Moon,
  Snacks: Cookie,
};

const Diary = () => {
  const { diary, goals, totals, exerciseLog, exerciseTotal, removeFood, removeExercise } = useApp();
  const navigate = useNavigate();
  const remaining = goals.calories - totals.calories + exerciseTotal;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="bg-white px-5 pt-5 pb-3 border-b border-gray-100 sticky top-0 z-30">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">Diary</h1>
          <button className="p-2 rounded-full hover:bg-gray-100">
            <Calendar size={20} className="text-gray-700" />
          </button>
        </div>
        <div className="flex items-center justify-between mt-3">
          <button className="p-1.5 rounded-full hover:bg-gray-100"><ChevronLeft size={20} /></button>
          <p className="text-sm font-semibold text-gray-900">Today, {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
          <button className="p-1.5 rounded-full hover:bg-gray-100"><ChevronRight size={20} /></button>
        </div>
      </header>

      {/* Summary card */}
      <div className="px-5 pt-4">
        <div className="bg-white rounded-2xl shadow-sm p-4 grid grid-cols-4 gap-2 text-center">
          <SumStat label="Goal" value={goals.calories} />
          <SumStat label="Food" value={totals.calories} />
          <SumStat label="Exercise" value={exerciseTotal} />
          <SumStat label="Remaining" value={remaining} highlight />
        </div>
      </div>

      {/* Meals */}
      <div className="px-5 pt-4 space-y-3 pb-4">
        {Object.keys(diary).map((meal) => {
          const Icon = mealIcons[meal] || Coffee;
          const items = diary[meal];
          const total = items.reduce((acc, m) => acc + (m.calories || 0) * (m.qty || 1), 0);
          return (
            <div key={meal} className="bg-white rounded-2xl shadow-sm overflow-hidden slide-up">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Icon size={18} className="text-[#0066ee]" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{meal}</p>
                    <p className="text-xs text-gray-500">{Math.round(total)} cal</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/add-food/${meal}`)}
                  className="flex items-center gap-1 text-[#0066ee] text-sm font-semibold px-3 py-1.5 rounded-full hover:bg-blue-50 press-scale"
                >
                  <Plus size={16} /> Add
                </button>
              </div>
              {items.length === 0 ? (
                <p className="px-4 py-3 text-sm text-gray-400 italic">No foods logged yet</p>
              ) : (
                <ul>
                  {items.map((item) => (
                    <li key={item.logId} className="flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-b-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.brand} · {item.qty || 1} × {item.serving}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-900">{Math.round((item.calories || 0) * (item.qty || 1))}</span>
                        <button onClick={() => removeFood(meal, item.logId)} className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}

        {/* Exercise section */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden slide-up">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Dumbbell size={18} className="text-emerald-600" />
              </div>
              <div>
                <p className="font-bold text-gray-900">Exercise</p>
                <p className="text-xs text-gray-500">{exerciseTotal} cal burned</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/add-exercise')}
              className="flex items-center gap-1 text-emerald-600 text-sm font-semibold px-3 py-1.5 rounded-full hover:bg-emerald-50 press-scale"
            >
              <Plus size={16} /> Add
            </button>
          </div>
          {exerciseLog.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-400 italic">No exercises logged</p>
          ) : (
            <ul>
              {exerciseLog.map((ex) => (
                <li key={ex.id} className="flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-b-0">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{ex.name}</p>
                    <p className="text-xs text-gray-500">{ex.minutes} min · {ex.time}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-emerald-600">-{ex.calories}</span>
                    <button onClick={() => removeExercise(ex.id)} className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <button className="w-full mt-2 bg-[#0066ee] text-white font-bold py-3.5 rounded-2xl shadow-md hover:bg-[#0055cc] press-scale">
          Complete Diary
        </button>
      </div>
    </div>
  );
};

const SumStat = ({ label, value, highlight }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className={`text-base font-bold mt-0.5 ${highlight ? 'text-[#0066ee]' : 'text-gray-900'}`}>
      {Math.round(value).toLocaleString()}
    </p>
  </div>
);

export default Diary;
